import { AxiosError } from 'axios';
import { Dispatch, SetStateAction, useCallback } from 'react';

import { useCustomNavigate } from 'hooks/useCustomNavigate';
import { SOMETHING_WENT_WRONG } from '../../constants/common';
import { UNABLE_TO_CONNECT_TO_SERVER } from '../../constants/onboarding';
import {
    BROWSER_LOCATION_CHANGE_REQUIRED,
    SECURITY_CSRF_VIOLATION,
    SECURITY_IDENTITY_MISMATCH,
    SELF_SERVICE_FLOW_EXPIRED,
    SELF_SERVICE_FLOW_RETURN_TO_FORBIDDEN,
    SESSION_ALREADY_AVAILABLE,
    SESSION_INACTIVE,
    SESSION_REFRESH_REQUIRED
} from '../../constants/ory';
import { HOME_ROUTE, LOGIN_ROUTE, SIGN_UP_ROUTE } from '../../constants/routes';
import { errorTextMap } from './error';

type ErrorMessage = { id: number; text: string };

export enum OryFlowType {
    LOGIN = 'login',
    SIGN_UP = 'sign-up',
    SETTINGS = 'settings',
    RECOVERY = 'recover-account',
    RESET_PASSWORD = 'reset-password',
    VERIFICATION = 'verification'
}

export const getErrorText = (message: ErrorMessage) => {
    if (message.id in errorTextMap) {
        return errorTextMap[message.id];
    }
    return message.text;
};

export const useOry = () => {
    const { navigate, navigateWithRedirectTo } = useCustomNavigate();

    const parseErrors = useCallback((err: AxiosError): string => {
        if (!err.response) {
            return '';
        }
        const { data } = err.response;
        const messages: string[] = [];

        data.ui.messages && messages.push(...data.ui.messages.map(getErrorText));

        for (const node of data.ui.nodes || []) {
            messages.push(...node.messages.map(getErrorText));
        }
        return messages[0] ?? '';
    }, []);

    const redirectTo = (redirectTo: string) => {
        const url = new URL(redirectTo);
        const navigateUrl = url.pathname + url.search;
        navigate(navigateUrl);
    };

    // A small function to help us deal with errors coming from ory.
    const handleError = useCallback(
        <S>(
            flowType: OryFlowType,
            resetFlow: Dispatch<SetStateAction<S | undefined>> = () => {
                return;
            },
            setError: (message: string) => void = () => {
                return;
            }
        ) => {
            return (err: AxiosError) => {
                if (!err.response) {
                    setError(UNABLE_TO_CONNECT_TO_SERVER);
                    navigateWithRedirectTo({ pathname: LOGIN_ROUTE });
                    return;
                }
                const errorID = err.response.data.error?.id;
                if (errorID) {
                    switch (errorID) {
                        case SESSION_INACTIVE:
                            navigateWithRedirectTo({ pathname: LOGIN_ROUTE });
                            return;
                        case SESSION_ALREADY_AVAILABLE:
                            navigate(HOME_ROUTE);
                            return;
                        case SESSION_REFRESH_REQUIRED:
                            redirectTo(err.response.data.redirect_browser_to);
                            return;
                        case SELF_SERVICE_FLOW_RETURN_TO_FORBIDDEN:
                            setError(SOMETHING_WENT_WRONG);
                            resetFlow(undefined);
                            navigate(`/${flowType}`);
                            return;
                        case SELF_SERVICE_FLOW_EXPIRED:
                            setError(SOMETHING_WENT_WRONG);
                            resetFlow(undefined);
                            navigate(`/${flowType}`);
                            return;
                        case SECURITY_CSRF_VIOLATION:
                            setError(SOMETHING_WENT_WRONG);
                            resetFlow(undefined);
                            navigate(`/${flowType}`);
                            return;
                        case SECURITY_IDENTITY_MISMATCH:
                            setError(SOMETHING_WENT_WRONG);
                            resetFlow(undefined);
                            navigate(`/${flowType}`);
                            return;
                        case BROWSER_LOCATION_CHANGE_REQUIRED:
                            redirectTo(err.response.data.redirect_browser_to);
                            return;
                    }
                }

                switch (err.response.status) {
                    case 401:
                        resetFlow(undefined);
                        navigateWithRedirectTo({ pathname: location.pathname == SIGN_UP_ROUTE ? SIGN_UP_ROUTE : LOGIN_ROUTE });
                        setError(err.response?.data?.error?.reason ?? 'Un Authorized Access');
                        return;
                    case 410:
                        resetFlow(undefined);
                        navigate(`/${flowType}`);
                        return;
                    case 404:
                        setError(SOMETHING_WENT_WRONG);
                        return;
                    case 400:
                        setError(parseErrors(err));
                        return;
                    default:
                        return;
                }
            };
        },
        [navigate, parseErrors]
    );

    return {
        handleError
    };
};
